import React, { useEffect } from "react";
import { Form, Input, Row, Col } from "antd";
import { useTranslation } from 'react-i18next';

const OwnerForm = props => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const isEditMode = props.isEditMode || false;

  const formItemLayout = {
    labelCol: {
      sm: { span: 6 }
    },
    wrapperCol: {
      sm: { span: 16 }
    }
  };

  // Initialize form with record data
  useEffect(() => {
    if (props.record.owner) {
      form.setFieldsValue({
        fullName: props.record.owner.fullName,
        fiscalCode: props.record.owner.fiscalCode,
        email: props.record.owner.email,
        phone: props.record.owner.phone
      });
    }
  }, [props.record.owner, form]);

  // Reset validation when exiting edit mode
  useEffect(() => {
    if (!isEditMode) {
      form.resetFields();
      
      if (props.record.owner) {
        form.setFieldsValue({
          fullName: props.record.owner.fullName,
          fiscalCode: props.record.owner.fiscalCode,
          email: props.record.owner.email,
          phone: props.record.owner.phone
        });
      }
    }
  }, [isEditMode, form, props.record.owner]);

  useEffect(() => {
    if (isEditMode && props.record.owner) {
      // Manually check validation status when in edit mode after initial render
      const values = form.getFieldsValue();
      const requiredFields = ['fullName', 'fiscalCode', 'email', 'phone'];
      const hasEmptyRequired = requiredFields.some(field => 
        !values[field] || (typeof values[field] === 'string' && values[field].trim() === '')
      );
      
      // Update parent with validation status
      if (hasEmptyRequired) {
        // Create formatted fields for parent callback
        const formattedFields = {};
        Object.keys(values).forEach(key => {
          formattedFields[key] = { value: values[key] };
        });
        props.parentCallback(formattedFields, false);
      }
    }
  }, [isEditMode]); // Only run when edit mode changes

  useEffect(() => {
    if (isEditMode && props.record.owner) {
      form.validateFields();
    }
  }, [isEditMode, form, props.record.owner]);

  const handleFormChange = () => {
    // Get current values without triggering additional validation
    const values = form.getFieldsValue();
    
    // Format for parent component
    const formattedFields = {};
    Object.keys(values).forEach(key => {
      formattedFields[key] = { value: values[key] };
    });
    
    // Only check validation in edit mode
    let isValid = true;
    if (isEditMode) {
      // Check for existing errors without triggering new validation
      const errors = form.getFieldsError();
      const hasErrors = errors.some(error => error.errors.length > 0);
      
      // Check for required empty fields more simply
      const requiredFields = ['fullName', 'fiscalCode', 'email', 'phone'];
      const hasEmptyRequired = requiredFields.some(field => 
        !values[field] || (typeof values[field] === 'string' && values[field].trim() === '')
      );
      
      isValid = !hasErrors && !hasEmptyRequired;
    }
    
    // Notify parent
    props.parentCallback(formattedFields, isValid);
  };

  return (
    <Form 
      form={form}
      {...formItemLayout}
      onValuesChange={handleFormChange}
      onFieldsChange={handleFormChange}
    >
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item 
            label="Full Name" 
            name="fullName"
            rules={isEditMode ? [
              { required: true, message: "Please input Full Name!" },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject('Full Name cannot be just whitespace');
                  }
                  return Promise.resolve();
                }
              }
            ] : []}
          >
            <Input disabled={!isEditMode} placeholder="Full Name" />
          </Form.Item>
          
          <Form.Item 
            label="Fiscal Code" 
            name="fiscalCode"
            rules={isEditMode ? [
              { required: true, message: "Please input Fiscal Code!" },
              {
                validator: (_, value) => {
                  if (value && value.trim() === '') {
                    return Promise.reject('Fiscal Code cannot be just whitespace');
                  }
                  return Promise.resolve();
                }
              }
            ] : []}
          >
            <Input disabled={!isEditMode} placeholder="Fiscal Code" />
          </Form.Item>
        </Col>
        
        <Col span={12}>
          <Form.Item 
            label="Email" 
            name="email"
            rules={isEditMode ? [
              { required: true, message: "Please input Email!" },
              { type: "email", message: "The input is not valid Email!" }
            ] : []}
          >
            <Input disabled={!isEditMode} placeholder="Email" />
          </Form.Item>
          
          <Form.Item 
            label="Phone" 
            name="phone"
            rules={isEditMode ? [
              { required: true, message: "Please input Phone!" },
              { 
                pattern: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/, 
                message: "The input is not valid Phone!" 
              }
            ] : []}
          >
            <Input disabled={!isEditMode} placeholder="Phone" />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default OwnerForm;